import { Children, cloneElement, forwardRef } from 'react';
import _ from 'lodash';
import Avatar from '@mui/material/Avatar';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import clsx from 'clsx';
import { IconButton, ListItemIcon } from '@mui/material';

const hoverStyle = {
  filter: 'brightness(1)',
  transition: 'filter 0.36s ease',
  cursor: 'pointer',
  '&:hover': {
    filter: 'brightness(0.92)',
  },
};

/**
 * ReferredListItem
 *
 * Componente de item de lista con soporte para imagen, icono y texto personalizados.
 *
 * @component
 * @param {string} [name] - Nombre para mostrar iniciales en el avatar
 * @param {React.ReactNode} [content] - Contenido personalizado
 * @param {string} [className] - Clases CSS adicionales para el item
 * @param {React.ReactNode} [children] - Elementos hijos a renderizar dentro del item
 * @param {boolean} [hover=false] - Si el item tiene efecto hover
 * @param {function} [onClick] - Callback al hacer click en el item
 * @param {object} [props] - Otras props para el ListItem
 * @param {React.Ref} ref - Referencia para el ListItem
 * @returns {JSX.Element}
 */
export const ReferredListItem = forwardRef(function ReferredListItem(
  { name, content, className, children, hover = false, onClick, ...props },
  ref
) {
  const childrenArray = Children.toArray(children);
  return (
    <>
      <ListItem
        ref={ref}
        className={clsx('px-32', className)}
        sx={{
          bgcolor: 'background.paper',
          ...(hover && hoverStyle),
        }}
        onClick={onClick}
        {...props}
      >
        {childrenArray.map((child, key) => cloneElement(child, { key: `buttonForm_${key}` }))}
      </ListItem>
      <Divider />
    </>
  );
});

const Image = ({ baseUri, image, name, color, sx, className, ...props }) => {
  return (
    <ListItemAvatar>
      <Avatar
        {...props}
        sx={{ bgcolor: color, ...sx }}
        className={clsx(className)}
        alt={name}
        src={`${baseUri || ''}${baseUri ? '/' : ''}${image}`}
      >
        {typeof name === 'string' &&
          name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .substring(0, 2)
            .toUpperCase()}
      </Avatar>
    </ListItemAvatar>
  );
};

const Icon = ({ children, className, buttonClasses, onClick, ...props }) => {
  return (
    <ListItemIcon className={clsx('min-w-40 -ml-10 mr-8', className)}>
      <IconButton className={clsx(buttonClasses)} onClick={onClick} {...props}>
        {children}
      </IconButton>
    </ListItemIcon>
  );
};

const contentDefaults = () => ({
  root: 'm-0',
  primary: 'truncate',
  secondary: undefined,
});

const entryClasses = {
  root: undefined,
  primary: undefined,
  secondary: undefined,
};

const Text = ({ className, primary, secondary, classes = entryClasses, ...props }) => {
  const classBuilder = _.merge({}, contentDefaults(), classes);
  return <ListItemText classes={classBuilder} primary={primary} secondary={secondary} {...props} />;
};

/**
 * Image
 *
 * Subcomponente para mostrar un avatar con imagen o iniciales.
 *
 * @component
 * @param {string} [baseUri] - Ruta base para la imagen
 * @param {string} [image] - Ruta de la imagen
 * @param {string} [name] - Nombre para iniciales
 * @param {string} [color] - Color de fondo del avatar
 * @param {object} [sx] - Estilos adicionales para el avatar
 * @param {string} [className] - Clases CSS adicionales
 * @returns {JSX.Element}
 */
ReferredListItem.Image = Image;

/**
 * Icon
 *
 * Subcomponente para mostrar un icono con botón dentro del item.
 *
 * @component
 * @param {React.ReactNode} children - Icono a mostrar
 * @param {string} [className] - Clases CSS adicionales para el ListItemIcon
 * @param {string} [buttonClasses] - Clases CSS adicionales para el IconButton
 * @param {function} [onClick] - Callback al hacer click en el icono
 * @returns {JSX.Element}
 */
ReferredListItem.Icon = Icon;

/**
 * Text
 *
 * Subcomponente para mostrar texto principal y secundario en el item.
 *
 * @component
 * @param {string} [className] - Clases CSS adicionales para el ListItemText
 * @param {string|React.ReactNode} primary - Texto principal
 * @param {string|React.ReactNode} [secondary] - Texto secundario
 * @param {object} [classes] - Clases para personalizar estilos
 * @returns {JSX.Element}
 */
ReferredListItem.Text = Text;
